/**
 * Competency Evaluation Service
 * 역량 평가 API 서비스
 */

import api from "./api";
import {
  ChecklistItem,
  CompetencyEvaluation,
  CompetencyEvaluationCreateRequest,
  CompetencyEvaluationSummary,
  PromotionJudgment,
  PromotionJudgmentCreateRequest,
  MyCompetencyStatus,
} from "../types/competency";

const BASE_URL = "/competency";

export const competencyService = {
  // 체크리스트 항목 조회
  getChecklistItems: async (job_family?: string, level?: string, include_inactive?: boolean): Promise<ChecklistItem[]> => {
    const params: any = {};
    if (job_family) params.job_family = job_family;
    if (level) params.level = level;
    if (include_inactive !== undefined) params.include_inactive = include_inactive;
    const response = await api.get(`${BASE_URL}/checklist-items`, { params });
    return response.data;
  },

  // 전체 체크리스트 항목 조회 (모든 직군)
  getAllChecklistItems: async (include_inactive?: boolean): Promise<ChecklistItem[]> => {
    const params: any = {};
    if (include_inactive !== undefined) params.include_inactive = include_inactive;
    // job_family 파라미터를 생략하면 모든 직군 조회
    const response = await api.get(`${BASE_URL}/checklist-items`, { params });
    return response.data;
  },

  // 체크리스트 항목 생성
  createChecklistItem: async (data: {
    job_family: string;
    level: string;
    category: string;
    item_code: string;
    description: string;
    order_num: number;
    is_active: boolean;
  }): Promise<ChecklistItem> => {
    const response = await api.post(`${BASE_URL}/checklist-items`, data);
    return response.data;
  },

  // 체크리스트 항목 수정
  updateChecklistItem: async (
    itemId: number,
    data: {
      job_family?: string;
      level?: string;
      category?: string;
      item_code?: string;
      description?: string;
      order_num?: number;
      is_active?: boolean;
    }
  ): Promise<ChecklistItem> => {
    const response = await api.put(`${BASE_URL}/checklist-items/${itemId}`, data);
    return response.data;
  },

  // 체크리스트 항목 삭제
  deleteChecklistItem: async (itemId: number): Promise<{ message: string }> => {
    const response = await api.delete(`${BASE_URL}/checklist-items/${itemId}`);
    return response.data;
  },

  // 체크리스트 항목 순서 변경
  reorderChecklistItem: async (
    itemId: number,
    direction: "up" | "down"
  ): Promise<{ message: string }> => {
    const response = await api.post(`${BASE_URL}/checklist-items/${itemId}/reorder`, null, {
      params: { direction },
    });
    return response.data;
  },

  // 체크리스트 항목 벌크 업데이트 (엑셀 업로드용)
  bulkUpdateChecklistItems: async (data: {
    job_family: string;
    items: Array<{
      id?: number;
      job_family: string;
      level: string;
      category: string;
      item_code: string;
      description: string;
      order_num: number;
      is_active: boolean;
    }>;
  }): Promise<{
    total: number;
    success: number;
    warning: number;
    error: number;
    results: Array<{
      id?: number;
      item_code: string;
      status: string;
      message: string;
      data?: ChecklistItem;
    }>;
  }> => {
    const response = await api.post(`${BASE_URL}/checklist-items/bulk-update`, data);
    return response.data;
  },

  // 내 역량 평가 현황 조회
  getMyStatus: async (year: number, quarter: number): Promise<MyCompetencyStatus> => {
    const response = await api.get(`${BASE_URL}/my-status`, {
      params: { year, quarter },
    });
    return response.data;
  },

  // 역량 평가 생성/수정
  createOrUpdateEvaluation: async (
    data: CompetencyEvaluationCreateRequest
  ): Promise<CompetencyEvaluation> => {
    const response = await api.post(`${BASE_URL}/evaluations`, data);
    return response.data;
  },

  // 역량 평가 제출
  submitEvaluation: async (evaluationId: number): Promise<CompetencyEvaluation> => {
    const response = await api.post(`${BASE_URL}/evaluations/${evaluationId}/submit`);
    return response.data;
  },

  // 역량 평가 집계 계산 (중앙값)
  calculateSummary: async (
    user_id: string,
    year: number,
    quarter: number
  ): Promise<CompetencyEvaluationSummary> => {
    const response = await api.post(`${BASE_URL}/evaluations/calculate-summary`, {
      user_id,
      year,
      quarter,
    });
    return response.data;
  },

  // 승급/강등 판정 생성
  createPromotionJudgment: async (
    data: PromotionJudgmentCreateRequest
  ): Promise<PromotionJudgment> => {
    const response = await api.post(`${BASE_URL}/promotion-judgment`, data);
    return response.data;
  },

  // ============ 평가 기간 관리 ============

  // 평가 기간 생성
  createEvaluationPeriod: async (data: {
    name: string;
    type: "regular" | "adhoc";
    year: number;
    quarter?: number;
    start_date: string;
    end_date: string;
    self_evaluation_required?: boolean;
    superior_levels?: number;
    peer_evaluation_enabled?: boolean;
    min_peer_count?: number;
    subordinate_evaluation_enabled?: boolean;
    min_evaluator_count?: number;
    description?: string;
    target_user_ids?: string[];
    target_department_ids?: string[];
  }): Promise<any> => {
    const response = await api.post("/evaluation-periods", data);
    return response.data;
  },

  // 평가 기간 목록 조회
  getEvaluationPeriods: async (params?: {
    status?: string;
    year?: number;
    quarter?: number;
  }): Promise<any[]> => {
    const response = await api.get("/evaluation-periods", { params });
    return response.data;
  },

  // 평가 기간 상세 조회
  getEvaluationPeriod: async (periodId: number): Promise<any> => {
    const response = await api.get(`/evaluation-periods/${periodId}`);
    return response.data;
  },

  // 평가 기간 수정
  updateEvaluationPeriod: async (periodId: number, data: any): Promise<any> => {
    const response = await api.patch(`/evaluation-periods/${periodId}`, data);
    return response.data;
  },

  // 평가 기간 삭제
  deleteEvaluationPeriod: async (periodId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/evaluation-periods/${periodId}`);
    return response.data;
  },

  // 평가자 자동 할당
  assignEvaluators: async (periodId: number, excludedEvaluatorIds?: string[]): Promise<{
    message: string;
    total_targets: number;
    total_assignments: number;
  }> => {
    const response = await api.post(`/evaluation-periods/${periodId}/assign-evaluators`, null, {
      params: excludedEvaluatorIds && excludedEvaluatorIds.length > 0
        ? { excluded_evaluator_ids: excludedEvaluatorIds }
        : {},
      // FastAPI Query(List[str]) expects repeated key (key=v1&key=v2), not bracket form (key[]=v1)
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  // 평가자 할당 현황 조회
  getAssignmentStatus: async (periodId: number): Promise<any> => {
    const response = await api.get(`/evaluation-periods/${periodId}/assignment-status`);
    return response.data;
  },

  // 평가 시작 (수동)
  startEvaluation: async (periodId: number): Promise<{
    message: string;
    period_id: number;
    period_name: string;
    status: string;
  }> => {
    const response = await api.post(`/evaluation-periods/${periodId}/start`);
    return response.data;
  },

  // 평가 일시 중단
  pauseEvaluation: async (periodId: number): Promise<{
    message: string;
    period_id: number;
    period_name: string;
    status: string;
  }> => {
    const response = await api.post(`/evaluation-periods/${periodId}/pause`);
    return response.data;
  },

  // 평가 강제 완료
  completeEvaluation: async (periodId: number): Promise<{
    message: string;
    period_id: number;
    period_name: string;
    status: string;
  }> => {
    const response = await api.post(`/evaluation-periods/${periodId}/complete`);
    return response.data;
  },

  // ============ 평가자 평가 수행 ============

  // 내가 평가해야 할 평가 목록
  getMyAssignments: async (status?: string): Promise<any[]> => {
    const response = await api.get(`${BASE_URL}/my-assignments`, {
      params: { status }
    });
    return response.data;
  },

  // 할당된 평가 제출 (1~5점 점수)
  submitAssignmentEvaluation: async (assignmentId: number, checklistResults: Record<string, number>): Promise<{
    message: string;
    evaluation_id: number;
    assignment_id: number;
  }> => {
    const response = await api.post(`${BASE_URL}/evaluate/${assignmentId}`, checklistResults);
    return response.data;
  },

  // 미완료 평가 개수 조회 (배너용)
  getMyPendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get(`${BASE_URL}/my-pending-count`);
    return response.data;
  },

  // 평가 대상자의 상세 결과 조회
  getTargetEvaluationResults: async (targetId: number): Promise<any> => {
    const response = await api.get(`/evaluation-periods/targets/${targetId}/results`);
    return response.data;
  },

  // ============ 개인 보고서 ============

  // 개인 평가 보고서 조회
  getPersonalReport: async (periodId: number, userId: string): Promise<any> => {
    const response = await api.get(`${BASE_URL}/report/${periodId}/${userId}`);
    return response.data;
  },

  // ============ 평가자 개별 추가/삭제 ============

  // 평가자 추가
  addEvaluator: async (targetId: number, evaluatorId: string, evaluatorType: string): Promise<{
    message: string;
    assignment_id: number;
    evaluator_name: string;
    evaluator_type: string;
  }> => {
    const response = await api.post(`/evaluation-periods/targets/${targetId}/evaluators`, null, {
      params: { evaluator_id: evaluatorId, evaluator_type: evaluatorType }
    });
    return response.data;
  },

  // 평가자 삭제
  deleteEvaluator: async (assignmentId: number): Promise<{
    message: string;
    assignment_id: number;
  }> => {
    const response = await api.delete(`/evaluation-periods/assignments/${assignmentId}`);
    return response.data;
  },
};
